// Translations for SecureVault
// Supported languages: English (en), French (fr), Haitian Creole (ht)

const translations = {
  en: {
    // Common
    common: {
      loading: "Loading...",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      view: "View",
      download: "Download",
      upload: "Upload",
      share: "Share",
      search: "Search",
      filter: "Filter",
      actions: "Actions",
      yes: "Yes",
      no: "No",
      confirm: "Confirm",
      back: "Back",
      next: "Next",
      submit: "Submit",
      close: "Close",
    },
    
    // Navigation
    nav: {
      dashboard: "Dashboard",
      myDocuments: "My Documents",
      sharedWithMe: "Shared With Me",
      userManagement: "User Management",
      auditLogs: "Audit Logs",
      settings: "Settings",
      logout: "Logout",
      login: "Login",
      register: "Get Started",
    },
    
    // Landing Page
    landing: {
      badge: "Enterprise-Grade Security",
      headline: "Your Documents,",
      headlineHighlight: "Encrypted & Protected",
      description: "SecureVault is a military-grade document management system with end-to-end encryption, role-based access control, and complete audit trails. Keep your confidential files safe from hackers.",
      ctaPrimary: "Start Securing Your Docs",
      ctaSecondary: "Sign In",
      featuresTitle: "Built for",
      featuresHighlight: "Maximum Security",
      featuresDescription: "Every feature is designed with security-first mindset. No compromises.",
      whyTrustTitle: "Why Trust",
      whyTrustHighlight: "SecureVault",
      trustPoints: [
        "AES-256 encryption - Same standard used by governments",
        "User-specific encryption keys for zero-trust architecture",
        "Complete audit trail of all actions",
        "Role-based access control with 4 permission levels",
        "Secure authentication with Google OAuth support",
        "Mobile-ready with biometric authentication support"
      ],
      ctaTitle: "Start Protecting Your Documents Today",
      ctaDescription: "Contact your administrator to get an invitation to this private document vault.",
      ctaButton: "Sign In",
      footer: "All rights reserved. Your documents, your security.",
    },
    
    // Features
    features: {
      encryption: {
        title: "End-to-End Encryption",
        description: "Your documents are encrypted with AES-256 user-specific keys. Only you and authorized users can access them."
      },
      roles: {
        title: "Role-Based Access",
        description: "Super Admin, Admin, Manager, and User roles with granular permission controls for every document."
      },
      audit: {
        title: "Complete Audit Trail",
        description: "Track every login, document access, and action. Know who accessed what and when."
      },
      auth: {
        title: "Secure Authentication",
        description: "Multi-factor authentication with Google OAuth and biometric support on mobile devices."
      },
      keys: {
        title: "User-Specific Keys",
        description: "Each user has their own encryption key derived from a master key. Maximum security, zero trust."
      },
      docs: {
        title: "All Document Types",
        description: "Upload PDFs, Word docs, Excel sheets, images, and more. All encrypted and secure."
      }
    },
    
    // Auth
    auth: {
      signIn: "Sign In",
      signUp: "Create Account",
      signInDescription: "Access your encrypted documents securely",
      signUpDescription: "Start securing your documents today",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      fullName: "Full Name",
      forgotPassword: "Forgot Password?",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      orContinueWith: "or continue with",
      googleSignIn: "Sign In with Google",
      googleSignUp: "Sign Up with Google",
      passwordRequirements: {
        length: "At least 8 characters",
        uppercase: "Contains uppercase letter",
        number: "Contains number"
      },
      twoFactor: {
        title: "Two-Factor Authentication",
        required: "2FA verification required",
        enterCode: "Enter verification code",
        invalidCode: "Invalid verification code",
        setupTotp: "Setup Authenticator App",
        setupEmail: "Setup Email OTP",
        scanQR: "Scan this QR code with your authenticator app",
        enterSetupCode: "Enter the code from your app to verify",
        emailOtpSent: "OTP sent to your email",
        disable: "Disable 2FA",
        enable: "Enable 2FA"
      }
    },
    
    // Dashboard
    dashboard: {
      welcome: "Welcome back",
      overview: "Your documents are encrypted and secure. Here's your vault overview.",
      myDocuments: "My Documents",
      sharedWithMe: "Shared With Me",
      totalUsers: "Total Users",
      loginsToday: "Logins Today",
      quickActions: "Quick Actions",
      uploadDocument: "Upload Document",
      viewShared: "View Shared",
      recentActivity: "Recent Activity",
      noActivity: "No recent activity",
      securityStatus: "Security Status",
      protected: "Protected",
      securityMessage: "All your documents are encrypted with AES-256. Your personal encryption key is active."
    },
    
    // Documents
    documents: {
      title: "My Documents",
      searchPlaceholder: "Search documents...",
      uploadButton: "Upload Document",
      uploadTitle: "Upload Document",
      uploadDescription: "Your document will be encrypted with AES-256 before storage.",
      dropzoneText: "Click to upload or drag and drop",
      dropzoneHint: "PDF, Word, Excel, Images up to 50MB",
      encryptionNote: "End-to-end encrypted with your personal key",
      noDocuments: "No Documents Yet",
      noDocumentsHint: "Upload your first document to get started",
      encrypted: "Encrypted",
      shareTitle: "Share Document",
      shareDescription: "Grant access to",
      addUser: "Add User",
      selectUser: "Select user",
      currentAccess: "Current Access",
      noAccess: "No one else has access to this document",
      grantAccess: "Grant Access",
      revokeAccess: "Revoke Access",
      permissions: {
        view: "View",
        edit: "Edit",
        download: "Download"
      }
    },
    
    // Shared Documents
    shared: {
      title: "Shared With Me",
      searchPlaceholder: "Search shared documents...",
      infoBanner: "These documents have been shared with you by other users. Your access level determines what actions you can perform.",
      noDocuments: "No Shared Documents",
      noDocumentsHint: "Documents shared with you will appear here",
      sharedBy: "Shared by"
    },
    
    // Users
    users: {
      title: "User Management",
      searchPlaceholder: "Search users...",
      totalUsers: "total users",
      roles: {
        superAdmin: "Super Admin",
        superAdminDesc: "Full system access",
        admin: "Admin",
        adminDesc: "User & document management",
        manager: "Manager",
        managerDesc: "Team document sharing",
        user: "User",
        userDesc: "Basic access"
      },
      editRole: "Edit Role",
      editRoleTitle: "Edit User Role",
      editRoleDescription: "Change role for",
      deleteUser: "Delete User",
      deleteConfirm: "Are you sure you want to delete this user? This action cannot be undone.",
      you: "(You)"
    },
    
    // Audit Logs
    auditLogs: {
      title: "Audit Logs",
      searchPlaceholder: "Search logs...",
      filterByAction: "Filter by action",
      allActions: "All Actions",
      logins: "Logins",
      registrations: "Registrations",
      uploads: "Uploads",
      downloads: "Downloads",
      deletions: "Deletions",
      accessGranted: "Access Granted",
      accessRevoked: "Access Revoked",
      infoBanner: "All user activities are logged and stored securely. This includes logins, document operations, and permission changes.",
      noLogs: "No Logs Found",
      noLogsHint: "No activity recorded yet"
    },
    
    // Settings
    settings: {
      title: "Settings",
      profile: {
        title: "Profile Information"
      },
      security: {
        title: "Security Status",
        encryption: "End-to-End Encryption",
        encryptionDesc: "Your personal encryption key is active",
        accountProtection: "Account Protection",
        accountProtectionDesc: "Your account is secured with",
        active: "Active",
        secure: "Secure"
      },
      password: {
        title: "Change Password",
        current: "Current Password",
        new: "New Password",
        confirm: "Confirm New Password",
        button: "Change Password",
        success: "Password changed successfully"
      },
      twoFactor: {
        title: "Two-Factor Authentication",
        description: "Add an extra layer of security to your account",
        enabled: "2FA is enabled",
        disabled: "2FA is disabled",
        method: "Method"
      },
      language: {
        title: "Language",
        description: "Choose your preferred language",
        english: "English",
        french: "French",
        creole: "Haitian Creole"
      },
      branding: {
        title: "Branding Settings",
        description: "Customize the application appearance (Super Admin only)",
        appName: "Application Name",
        logo: "Logo",
        uploadLogo: "Upload Logo",
        currentLogo: "Current Logo",
        noLogo: "No custom logo set"
      },
      encryptionInfo: {
        title: "About Your Encryption",
        description: "All your documents are encrypted using AES-256 encryption with a unique key derived from your account. This means only you and authorized users can access your documents. Even system administrators cannot read your encrypted files without your permission."
      }
    },
    
    // Errors
    errors: {
      generic: "Something went wrong",
      unauthorized: "Unauthorized access",
      notFound: "Not found",
      invalidCredentials: "Invalid credentials",
      emailExists: "Email already registered",
      weakPassword: "Password is too weak",
      passwordMismatch: "Passwords do not match",
      uploadFailed: "Upload failed",
      downloadFailed: "Download failed",
      accessDenied: "Access denied"
    },
    
    // Success messages
    success: {
      documentUploaded: "Document uploaded and encrypted!",
      documentDeleted: "Document deleted!",
      documentDownloaded: "Document downloaded!",
      accessGranted: "Access granted!",
      accessRevoked: "Access revoked!",
      userUpdated: "User role updated!",
      userDeleted: "User deleted!",
      settingsSaved: "Settings saved!",
      loggedOut: "Logged out successfully"
    },

    // Calendar
    calendar: {
      title: "Calendar",
      scheduleMeeting: "Schedule Meeting",
      editMeeting: "Edit Meeting",
      meetingTitle: "Meeting Title",
      description: "Description",
      startTime: "Start Time",
      endTime: "End Time",
      location: "Location (optional)",
      includeVideoCall: "Include Video Call Link",
      inviteType: "Invite",
      allUsers: "All Users",
      byRole: "By Role",
      selectRoles: "Select Roles",
      specificPeople: "Specific People",
      selectPeople: "Select People",
      recurrence: "Recurrence",
      doesNotRepeat: "Does not repeat",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      every: "Every",
      until: "Until",
      attendees: "Attendees",
      organizedBy: "Organized by",
      noMeetings: "No meetings scheduled for this day",
      joinMeeting: "Join Meeting",
      deleteMeeting: "Delete Meeting",
      meetingTitlePlaceholder: "Team Standup",
      descriptionPlaceholder: "Discuss project updates...",
      locationPlaceholder: "Conference Room A"
    },

    // Tasks
    tasks: {
      title: "Tasks",
      createTask: "Create Task",
      editTask: "Edit Task",
      taskTitle: "Task Title",
      description: "Description",
      assignTo: "Assign To",
      dueDate: "Due Date",
      priority: "Priority",
      status: "Status",
      linkedDocument: "Linked Document",
      allTasks: "All Tasks",
      assignedToMe: "Assigned to Me",
      createdByMe: "Created by Me",
      allStatuses: "All Statuses",
      allPriorities: "All Priorities",
      filterBy: "Filter by",
      noTasks: "No tasks found",
      noTasksHint: "Create a new task to get started",
      total: "Total",
      overdue: "Overdue",
      pending: "Pending",
      inProgress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
      low: "Low",
      medium: "Medium",
      high: "High",
      none: "None",
      selectDocument: "Select document",
      selectTeamMember: "Select team member",
      titlePlaceholder: "Review quarterly report",
      descriptionPlaceholder: "Add more details..."
    }
  },
  
  fr: {
    // Common
    common: {
      loading: "Chargement...",
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
      view: "Voir",
      download: "Télécharger",
      upload: "Téléverser",
      share: "Partager",
      search: "Rechercher",
      filter: "Filtrer",
      actions: "Actions",
      yes: "Oui",
      no: "Non",
      confirm: "Confirmer",
      back: "Retour",
      next: "Suivant",
      submit: "Soumettre",
      close: "Fermer",
    },
    
    // Navigation
    nav: {
      dashboard: "Tableau de bord",
      myDocuments: "Mes Documents",
      sharedWithMe: "Partagés avec moi",
      userManagement: "Gestion des utilisateurs",
      auditLogs: "Journaux d'audit",
      settings: "Paramètres",
      logout: "Déconnexion",
      login: "Connexion",
      register: "Commencer",
    },
    
    // Landing Page
    landing: {
      badge: "Sécurité de niveau entreprise",
      headline: "Vos Documents,",
      headlineHighlight: "Chiffrés et Protégés",
      description: "SecureVault est un système de gestion de documents de niveau militaire avec chiffrement de bout en bout, contrôle d'accès basé sur les rôles et pistes d'audit complètes. Gardez vos fichiers confidentiels à l'abri des pirates.",
      ctaPrimary: "Commencer à sécuriser vos docs",
      ctaSecondary: "Se connecter",
      featuresTitle: "Conçu pour une",
      featuresHighlight: "Sécurité Maximale",
      featuresDescription: "Chaque fonctionnalité est conçue avec une mentalité de sécurité d'abord. Sans compromis.",
      whyTrustTitle: "Pourquoi faire confiance à",
      whyTrustHighlight: "SecureVault",
      trustPoints: [
        "Chiffrement AES-256 - La même norme utilisée par les gouvernements",
        "Clés de chiffrement spécifiques à l'utilisateur pour une architecture zero-trust",
        "Piste d'audit complète de toutes les actions",
        "Contrôle d'accès basé sur les rôles avec 4 niveaux de permission",
        "Authentification sécurisée avec support Google OAuth",
        "Prêt pour mobile avec support d'authentification biométrique"
      ],
      ctaTitle: "Commencez à protéger vos documents aujourd'hui",
      ctaDescription: "Contactez votre administrateur pour obtenir une invitation à ce coffre-fort privé.",
      ctaButton: "Se connecter",
      footer: "Tous droits réservés. Vos documents, votre sécurité.",
    },
    
    // Features
    features: {
      encryption: {
        title: "Chiffrement de bout en bout",
        description: "Vos documents sont chiffrés avec des clés AES-256 spécifiques à l'utilisateur. Seuls vous et les utilisateurs autorisés peuvent y accéder."
      },
      roles: {
        title: "Accès basé sur les rôles",
        description: "Rôles Super Admin, Admin, Manager et Utilisateur avec contrôles de permission granulaires pour chaque document."
      },
      audit: {
        title: "Piste d'audit complète",
        description: "Suivez chaque connexion, accès aux documents et action. Sachez qui a accédé à quoi et quand."
      },
      auth: {
        title: "Authentification sécurisée",
        description: "Authentification multi-facteurs avec Google OAuth et support biométrique sur appareils mobiles."
      },
      keys: {
        title: "Clés spécifiques à l'utilisateur",
        description: "Chaque utilisateur a sa propre clé de chiffrement dérivée d'une clé maître. Sécurité maximale, confiance zéro."
      },
      docs: {
        title: "Tous types de documents",
        description: "Téléversez des PDF, documents Word, feuilles Excel, images et plus. Tout est chiffré et sécurisé."
      }
    },
    
    // Auth
    auth: {
      signIn: "Se connecter",
      signUp: "Créer un compte",
      signInDescription: "Accédez à vos documents chiffrés en toute sécurité",
      signUpDescription: "Commencez à sécuriser vos documents aujourd'hui",
      email: "Email",
      password: "Mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      fullName: "Nom complet",
      forgotPassword: "Mot de passe oublié?",
      noAccount: "Vous n'avez pas de compte?",
      hasAccount: "Vous avez déjà un compte?",
      orContinueWith: "ou continuer avec",
      googleSignIn: "Se connecter avec Google",
      googleSignUp: "S'inscrire avec Google",
      passwordRequirements: {
        length: "Au moins 8 caractères",
        uppercase: "Contient une majuscule",
        number: "Contient un chiffre"
      },
      twoFactor: {
        title: "Authentification à deux facteurs",
        required: "Vérification 2FA requise",
        enterCode: "Entrez le code de vérification",
        invalidCode: "Code de vérification invalide",
        setupTotp: "Configurer l'application d'authentification",
        setupEmail: "Configurer l'OTP par email",
        scanQR: "Scannez ce code QR avec votre application d'authentification",
        enterSetupCode: "Entrez le code de votre application pour vérifier",
        emailOtpSent: "OTP envoyé à votre email",
        disable: "Désactiver 2FA",
        enable: "Activer 2FA"
      }
    },
    
    // Dashboard
    dashboard: {
      welcome: "Bon retour",
      overview: "Vos documents sont chiffrés et sécurisés. Voici l'aperçu de votre coffre-fort.",
      myDocuments: "Mes Documents",
      sharedWithMe: "Partagés avec moi",
      totalUsers: "Utilisateurs totaux",
      loginsToday: "Connexions aujourd'hui",
      quickActions: "Actions rapides",
      uploadDocument: "Téléverser un document",
      viewShared: "Voir les partagés",
      recentActivity: "Activité récente",
      noActivity: "Aucune activité récente",
      securityStatus: "État de sécurité",
      protected: "Protégé",
      securityMessage: "Tous vos documents sont chiffrés avec AES-256. Votre clé de chiffrement personnelle est active."
    },
    
    // Documents
    documents: {
      title: "Mes Documents",
      searchPlaceholder: "Rechercher des documents...",
      uploadButton: "Téléverser un document",
      uploadTitle: "Téléverser un document",
      uploadDescription: "Votre document sera chiffré avec AES-256 avant stockage.",
      dropzoneText: "Cliquez pour téléverser ou glissez-déposez",
      dropzoneHint: "PDF, Word, Excel, Images jusqu'à 50 Mo",
      encryptionNote: "Chiffré de bout en bout avec votre clé personnelle",
      noDocuments: "Pas encore de documents",
      noDocumentsHint: "Téléversez votre premier document pour commencer",
      encrypted: "Chiffré",
      shareTitle: "Partager le document",
      shareDescription: "Accorder l'accès à",
      addUser: "Ajouter un utilisateur",
      selectUser: "Sélectionner l'utilisateur",
      currentAccess: "Accès actuel",
      noAccess: "Personne d'autre n'a accès à ce document",
      grantAccess: "Accorder l'accès",
      revokeAccess: "Révoquer l'accès",
      permissions: {
        view: "Voir",
        edit: "Modifier",
        download: "Télécharger"
      }
    },
    
    // Shared Documents
    shared: {
      title: "Partagés avec moi",
      searchPlaceholder: "Rechercher des documents partagés...",
      infoBanner: "Ces documents ont été partagés avec vous par d'autres utilisateurs. Votre niveau d'accès détermine les actions que vous pouvez effectuer.",
      noDocuments: "Pas de documents partagés",
      noDocumentsHint: "Les documents partagés avec vous apparaîtront ici",
      sharedBy: "Partagé par"
    },
    
    // Users
    users: {
      title: "Gestion des utilisateurs",
      searchPlaceholder: "Rechercher des utilisateurs...",
      totalUsers: "utilisateurs au total",
      roles: {
        superAdmin: "Super Admin",
        superAdminDesc: "Accès système complet",
        admin: "Admin",
        adminDesc: "Gestion des utilisateurs et documents",
        manager: "Manager",
        managerDesc: "Partage de documents d'équipe",
        user: "Utilisateur",
        userDesc: "Accès de base"
      },
      editRole: "Modifier le rôle",
      editRoleTitle: "Modifier le rôle de l'utilisateur",
      editRoleDescription: "Changer le rôle pour",
      deleteUser: "Supprimer l'utilisateur",
      deleteConfirm: "Êtes-vous sûr de vouloir supprimer cet utilisateur? Cette action est irréversible.",
      you: "(Vous)"
    },
    
    // Audit Logs
    auditLogs: {
      title: "Journaux d'audit",
      searchPlaceholder: "Rechercher dans les journaux...",
      filterByAction: "Filtrer par action",
      allActions: "Toutes les actions",
      logins: "Connexions",
      registrations: "Inscriptions",
      uploads: "Téléversements",
      downloads: "Téléchargements",
      deletions: "Suppressions",
      accessGranted: "Accès accordé",
      accessRevoked: "Accès révoqué",
      infoBanner: "Toutes les activités des utilisateurs sont enregistrées et stockées en toute sécurité. Cela inclut les connexions, les opérations sur les documents et les changements de permissions.",
      noLogs: "Aucun journal trouvé",
      noLogsHint: "Aucune activité enregistrée pour le moment"
    },
    
    // Settings
    settings: {
      title: "Paramètres",
      profile: {
        title: "Informations du profil"
      },
      security: {
        title: "État de sécurité",
        encryption: "Chiffrement de bout en bout",
        encryptionDesc: "Votre clé de chiffrement personnelle est active",
        accountProtection: "Protection du compte",
        accountProtectionDesc: "Votre compte est sécurisé avec",
        active: "Actif",
        secure: "Sécurisé"
      },
      password: {
        title: "Changer le mot de passe",
        current: "Mot de passe actuel",
        new: "Nouveau mot de passe",
        confirm: "Confirmer le nouveau mot de passe",
        button: "Changer le mot de passe",
        success: "Mot de passe changé avec succès"
      },
      twoFactor: {
        title: "Authentification à deux facteurs",
        description: "Ajoutez une couche de sécurité supplémentaire à votre compte",
        enabled: "2FA est activé",
        disabled: "2FA est désactivé",
        method: "Méthode"
      },
      language: {
        title: "Langue",
        description: "Choisissez votre langue préférée",
        english: "Anglais",
        french: "Français",
        creole: "Créole haïtien"
      },
      branding: {
        title: "Paramètres de marque",
        description: "Personnalisez l'apparence de l'application (Super Admin uniquement)",
        appName: "Nom de l'application",
        logo: "Logo",
        uploadLogo: "Téléverser le logo",
        currentLogo: "Logo actuel",
        noLogo: "Aucun logo personnalisé défini"
      },
      encryptionInfo: {
        title: "À propos de votre chiffrement",
        description: "Tous vos documents sont chiffrés avec le chiffrement AES-256 avec une clé unique dérivée de votre compte. Cela signifie que seuls vous et les utilisateurs autorisés pouvez accéder à vos documents. Même les administrateurs système ne peuvent pas lire vos fichiers chiffrés sans votre permission."
      }
    },
    
    // Errors
    errors: {
      generic: "Une erreur s'est produite",
      unauthorized: "Accès non autorisé",
      notFound: "Non trouvé",
      invalidCredentials: "Identifiants invalides",
      emailExists: "Email déjà enregistré",
      weakPassword: "Mot de passe trop faible",
      passwordMismatch: "Les mots de passe ne correspondent pas",
      uploadFailed: "Échec du téléversement",
      downloadFailed: "Échec du téléchargement",
      accessDenied: "Accès refusé"
    },
    
    // Success messages
    success: {
      documentUploaded: "Document téléversé et chiffré!",
      documentDeleted: "Document supprimé!",
      documentDownloaded: "Document téléchargé!",
      accessGranted: "Accès accordé!",
      accessRevoked: "Accès révoqué!",
      userUpdated: "Rôle de l'utilisateur mis à jour!",
      userDeleted: "Utilisateur supprimé!",
      settingsSaved: "Paramètres enregistrés!",
      loggedOut: "Déconnecté avec succès"
    },

    // Calendar
    calendar: {
      title: "Calendrier",
      scheduleMeeting: "Planifier une réunion",
      editMeeting: "Modifier la réunion",
      meetingTitle: "Titre de la réunion",
      description: "Description",
      startTime: "Heure de début",
      endTime: "Heure de fin",
      location: "Lieu (optionnel)",
      includeVideoCall: "Inclure un lien d'appel vidéo",
      inviteType: "Inviter",
      allUsers: "Tous les utilisateurs",
      byRole: "Par rôle",
      selectRoles: "Sélectionner les rôles",
      specificPeople: "Personnes spécifiques",
      selectPeople: "Sélectionner des personnes",
      recurrence: "Récurrence",
      doesNotRepeat: "Ne se répète pas",
      daily: "Quotidien",
      weekly: "Hebdomadaire",
      monthly: "Mensuel",
      every: "Chaque",
      until: "Jusqu'à",
      attendees: "Participants",
      organizedBy: "Organisé par",
      noMeetings: "Aucune réunion prévue pour cette journée",
      joinMeeting: "Rejoindre la réunion",
      deleteMeeting: "Supprimer la réunion",
      meetingTitlePlaceholder: "Réunion d'équipe",
      descriptionPlaceholder: "Discuter des mises à jour du projet...",
      locationPlaceholder: "Salle de conférence A"
    },

    // Tasks
    tasks: {
      title: "Tâches",
      createTask: "Créer une tâche",
      editTask: "Modifier la tâche",
      taskTitle: "Titre de la tâche",
      description: "Description",
      assignTo: "Assigner à",
      dueDate: "Date d'échéance",
      priority: "Priorité",
      status: "Statut",
      linkedDocument: "Document lié",
      allTasks: "Toutes les tâches",
      assignedToMe: "Assignées à moi",
      createdByMe: "Créées par moi",
      allStatuses: "Tous les statuts",
      allPriorities: "Toutes les priorités",
      filterBy: "Filtrer par",
      noTasks: "Aucune tâche trouvée",
      noTasksHint: "Créez une nouvelle tâche pour commencer",
      total: "Total",
      overdue: "En retard",
      pending: "En attente",
      inProgress: "En cours",
      completed: "Terminé",
      cancelled: "Annulé",
      low: "Faible",
      medium: "Moyen",
      high: "Élevé",
      none: "Aucun",
      selectDocument: "Sélectionner un document",
      selectTeamMember: "Sélectionner un membre de l'équipe",
      titlePlaceholder: "Examiner le rapport trimestriel",
      descriptionPlaceholder: "Ajouter plus de détails..."
    }
  },
  
  ht: {
    // Common - Haitian Creole
    common: {
      loading: "Ap chaje...",
      save: "Anrejistre",
      cancel: "Anile",
      delete: "Efase",
      edit: "Modifye",
      view: "Gade",
      download: "Telechaje",
      upload: "Voye",
      share: "Pataje",
      search: "Chèche",
      filter: "Filtre",
      actions: "Aksyon",
      yes: "Wi",
      no: "Non",
      confirm: "Konfime",
      back: "Retounen",
      next: "Pwochen",
      submit: "Soumèt",
      close: "Fèmen",
    },
    
    // Navigation
    nav: {
      dashboard: "Tablo Bò",
      myDocuments: "Dokiman Mwen",
      sharedWithMe: "Pataje avèk mwen",
      userManagement: "Jesyon Itilizatè",
      auditLogs: "Jounal Odit",
      settings: "Paramèt",
      logout: "Dekonekte",
      login: "Konekte",
      register: "Kòmanse",
    },
    
    // Landing Page
    landing: {
      badge: "Sekirite Nivo Antrepriz",
      headline: "Dokiman Ou,",
      headlineHighlight: "Chifre & Pwoteje",
      description: "SecureVault se yon sistèm jesyon dokiman nivo militè ak chifreman bout an bout, kontwòl aksè baze sou wòl, ak tras odit konplè. Kenbe fichye konfidansyèl ou yo an sekirite kont pirat.",
      ctaPrimary: "Kòmanse Pwoteje Dokiman Ou",
      ctaSecondary: "Konekte",
      featuresTitle: "Konstwi pou",
      featuresHighlight: "Sekirite Maksimòm",
      featuresDescription: "Chak karakteristik fèt ak yon mantalite sekirite an premye. Pa gen okenn konpwomi.",
      whyTrustTitle: "Poukisa Fè Konfyans",
      whyTrustHighlight: "SecureVault",
      trustPoints: [
        "Chifreman AES-256 - Menm estanda gouvènman yo itilize",
        "Kle chifreman espesifik pou itilizatè pou yon achitekti zewo-konfyans",
        "Tras odit konplè pou tout aksyon",
        "Kontwòl aksè baze sou wòl ak 4 nivo pèmisyon",
        "Otantifikasyon sekirize ak sipò Google OAuth",
        "Pare pou mobil ak sipò otantifikasyon byometrik"
      ],
      ctaTitle: "Kòmanse Pwoteje Dokiman Ou Jodi a",
      ctaDescription: "Kontakte administratè ou pou jwenn yon envitasyon nan kòf prive sa a.",
      ctaButton: "Konekte",
      footer: "Tout dwa rezève. Dokiman ou, sekirite ou.",
    },
    
    // Features
    features: {
      encryption: {
        title: "Chifreman Bout an Bout",
        description: "Dokiman ou yo chifre ak kle AES-256 espesifik pou itilizatè. Sèlman ou menm ak itilizatè otorize ka jwenn aksè nan yo."
      },
      roles: {
        title: "Aksè Baze sou Wòl",
        description: "Wòl Super Admin, Admin, Manadjè, ak Itilizatè ak kontwòl pèmisyon granlè pou chak dokiman."
      },
      audit: {
        title: "Tras Odit Konplè",
        description: "Swiv chak koneksyon, aksè nan dokiman, ak aksyon. Konnen kiyès ki te jwenn aksè nan ki sa ak kilè."
      },
      auth: {
        title: "Otantifikasyon Sekirize",
        description: "Otantifikasyon milti-faktè ak Google OAuth ak sipò byometrik sou aparèy mobil."
      },
      keys: {
        title: "Kle Espesifik pou Itilizatè",
        description: "Chak itilizatè gen pwòp kle chifreman li derive de yon kle mèt. Sekirite maksimòm, zewo konfyans."
      },
      docs: {
        title: "Tout Kalite Dokiman",
        description: "Voye PDF, dokiman Word, fèy Excel, imaj, ak plis. Tout chifre ak sekirize."
      }
    },
    
    // Auth
    auth: {
      signIn: "Konekte",
      signUp: "Kreye Kont",
      signInDescription: "Jwenn aksè nan dokiman chifre ou yo an sekirite",
      signUpDescription: "Kòmanse sekirize dokiman ou yo jodi a",
      email: "Imèl",
      password: "Modpas",
      confirmPassword: "Konfime Modpas",
      fullName: "Non Konplè",
      forgotPassword: "Bliye Modpas?",
      noAccount: "Ou pa gen kont?",
      hasAccount: "Ou gen deja yon kont?",
      orContinueWith: "oswa kontinye ak",
      googleSignIn: "Konekte ak Google",
      googleSignUp: "Enskri ak Google",
      passwordRequirements: {
        length: "Omwen 8 karaktè",
        uppercase: "Gen lèt majiskil",
        number: "Gen nimewo"
      },
      twoFactor: {
        title: "Otantifikasyon De-Faktè",
        required: "Verifikasyon 2FA obligatwa",
        enterCode: "Antre kòd verifikasyon",
        invalidCode: "Kòd verifikasyon envalid",
        setupTotp: "Konfigire Aplikasyon Otantikatè",
        setupEmail: "Konfigire OTP Imèl",
        scanQR: "Eskane kòd QR sa a ak aplikasyon otantikatè ou",
        enterSetupCode: "Antre kòd nan aplikasyon ou pou verifye",
        emailOtpSent: "OTP voye nan imèl ou",
        disable: "Dezaktive 2FA",
        enable: "Aktive 2FA"
      }
    },
    
    // Dashboard
    dashboard: {
      welcome: "Byenveni ankò",
      overview: "Dokiman ou yo chifre ak sekirize. Men apèsi kòf fò ou.",
      myDocuments: "Dokiman Mwen",
      sharedWithMe: "Pataje avèk mwen",
      totalUsers: "Total Itilizatè",
      loginsToday: "Koneksyon Jodi a",
      quickActions: "Aksyon Rapid",
      uploadDocument: "Voye Dokiman",
      viewShared: "Gade Pataje",
      recentActivity: "Aktivite Resan",
      noActivity: "Pa gen aktivite resan",
      securityStatus: "Eta Sekirite",
      protected: "Pwoteje",
      securityMessage: "Tout dokiman ou yo chifre ak AES-256. Kle chifreman pèsonèl ou aktif."
    },
    
    // Documents
    documents: {
      title: "Dokiman Mwen",
      searchPlaceholder: "Chèche dokiman...",
      uploadButton: "Voye Dokiman",
      uploadTitle: "Voye Dokiman",
      uploadDescription: "Dokiman ou a ap chifre ak AES-256 anvan estokaj.",
      dropzoneText: "Klike pou voye oswa trennen ak depoze",
      dropzoneHint: "PDF, Word, Excel, Imaj jiska 50MB",
      encryptionNote: "Chifre bout an bout ak kle pèsonèl ou",
      noDocuments: "Pa Gen Dokiman Ankò",
      noDocumentsHint: "Voye premye dokiman ou pou kòmanse",
      encrypted: "Chifre",
      shareTitle: "Pataje Dokiman",
      shareDescription: "Bay aksè nan",
      addUser: "Ajoute Itilizatè",
      selectUser: "Chwazi itilizatè",
      currentAccess: "Aksè Aktyèl",
      noAccess: "Pèsonn lòt pa gen aksè nan dokiman sa a",
      grantAccess: "Bay Aksè",
      revokeAccess: "Retire Aksè",
      permissions: {
        view: "Gade",
        edit: "Modifye",
        download: "Telechaje"
      }
    },
    
    // Shared Documents
    shared: {
      title: "Pataje avèk Mwen",
      searchPlaceholder: "Chèche dokiman pataje...",
      infoBanner: "Dokiman sa yo te pataje ak ou pa lòt itilizatè. Nivo aksè ou detèmine ki aksyon ou ka fè.",
      noDocuments: "Pa Gen Dokiman Pataje",
      noDocumentsHint: "Dokiman pataje ak ou ap parèt isit la",
      sharedBy: "Pataje pa"
    },
    
    // Users
    users: {
      title: "Jesyon Itilizatè",
      searchPlaceholder: "Chèche itilizatè...",
      totalUsers: "itilizatè total",
      roles: {
        superAdmin: "Super Admin",
        superAdminDesc: "Aksè sistèm konplè",
        admin: "Admin",
        adminDesc: "Jesyon itilizatè ak dokiman",
        manager: "Manadjè",
        managerDesc: "Pataj dokiman ekip",
        user: "Itilizatè",
        userDesc: "Aksè debaz"
      },
      editRole: "Modifye Wòl",
      editRoleTitle: "Modifye Wòl Itilizatè",
      editRoleDescription: "Chanje wòl pou",
      deleteUser: "Efase Itilizatè",
      deleteConfirm: "Èske ou sèten ou vle efase itilizatè sa a? Aksyon sa a pa ka defèt.",
      you: "(Ou)"
    },
    
    // Audit Logs
    auditLogs: {
      title: "Jounal Odit",
      searchPlaceholder: "Chèche nan jounal...",
      filterByAction: "Filtre pa aksyon",
      allActions: "Tout Aksyon",
      logins: "Koneksyon",
      registrations: "Enskripsyon",
      uploads: "Voye",
      downloads: "Telechaje",
      deletions: "Efase",
      accessGranted: "Aksè Bay",
      accessRevoked: "Aksè Retire",
      infoBanner: "Tout aktivite itilizatè anrejistre ak estoke an sekirite. Sa enkli koneksyon, operasyon sou dokiman, ak chanjman pèmisyon.",
      noLogs: "Pa Gen Jounal Jwenn",
      noLogsHint: "Pa gen aktivite anrejistre pou kounye a"
    },
    
    // Settings
    settings: {
      title: "Paramèt",
      profile: {
        title: "Enfòmasyon Pwofil"
      },
      security: {
        title: "Eta Sekirite",
        encryption: "Chifreman Bout an Bout",
        encryptionDesc: "Kle chifreman pèsonèl ou aktif",
        accountProtection: "Pwoteksyon Kont",
        accountProtectionDesc: "Kont ou sekirize ak",
        active: "Aktif",
        secure: "Sekirize"
      },
      password: {
        title: "Chanje Modpas",
        current: "Modpas Aktyèl",
        new: "Nouvo Modpas",
        confirm: "Konfime Nouvo Modpas",
        button: "Chanje Modpas",
        success: "Modpas chanje avèk siksè"
      },
      twoFactor: {
        title: "Otantifikasyon De-Faktè",
        description: "Ajoute yon kouch sekirite siplemantè nan kont ou",
        enabled: "2FA aktive",
        disabled: "2FA dezaktive",
        method: "Metòd"
      },
      language: {
        title: "Lang",
        description: "Chwazi lang ou prefere",
        english: "Angle",
        french: "Franse",
        creole: "Kreyòl Ayisyen"
      },
      branding: {
        title: "Paramèt Mak",
        description: "Pèsonalize aparans aplikasyon an (Super Admin sèlman)",
        appName: "Non Aplikasyon",
        logo: "Logo",
        uploadLogo: "Voye Logo",
        currentLogo: "Logo Aktyèl",
        noLogo: "Pa gen logo pèsonalize defini"
      },
      encryptionInfo: {
        title: "Konsènan Chifreman Ou",
        description: "Tout dokiman ou yo chifre ak chifreman AES-256 ak yon kle inik derive de kont ou. Sa vle di sèlman ou menm ak itilizatè otorize ka jwenn aksè nan dokiman ou yo. Menm administratè sistèm pa ka li fichye chifre ou yo san pèmisyon ou."
      }
    },
    
    // Errors
    errors: {
      generic: "Yon bagay mal pase",
      unauthorized: "Aksè san otorizasyon",
      notFound: "Pa jwenn",
      invalidCredentials: "Idantifyan envalid",
      emailExists: "Imèl deja anrejistre",
      weakPassword: "Modpas twò fèb",
      passwordMismatch: "Modpas yo pa matche",
      uploadFailed: "Voye echwe",
      downloadFailed: "Telechajman echwe",
      accessDenied: "Aksè refize"
    },
    
    // Success messages
    success: {
      documentUploaded: "Dokiman voye ak chifre!",
      documentDeleted: "Dokiman efase!",
      documentDownloaded: "Dokiman telechaje!",
      accessGranted: "Aksè bay!",
      accessRevoked: "Aksè retire!",
      userUpdated: "Wòl itilizatè mete ajou!",
      userDeleted: "Itilizatè efase!",
      settingsSaved: "Paramèt anrejistre!",
      loggedOut: "Dekonekte avèk siksè"
    },

    // Calendar
    calendar: {
      title: "Kalandriye",
      scheduleMeeting: "Planifye Reyinyon",
      editMeeting: "Modifye Reyinyon",
      meetingTitle: "Tit Reyinyon",
      description: "Deskripsyon",
      startTime: "Lè Kòmanse",
      endTime: "Lè Fini",
      location: "Kote (opsyonèl)",
      includeVideoCall: "Mete Lyen Apèl Videyo",
      inviteType: "Envite",
      allUsers: "Tout Itilizatè",
      byRole: "Pa Wòl",
      selectRoles: "Chwazi Wòl",
      specificPeople: "Moun Espesifik",
      selectPeople: "Chwazi Moun",
      recurrence: "Repete",
      doesNotRepeat: "Pa repete",
      daily: "Chak Jou",
      weekly: "Chak Semèn",
      monthly: "Chak Mwa",
      every: "Chak",
      until: "Jiska",
      attendees: "Patisipan",
      organizedBy: "Òganize pa",
      noMeetings: "Pa gen reyinyon planifye pou jou sa a",
      joinMeeting: "Rantre nan Reyinyon",
      deleteMeeting: "Efase Reyinyon",
      meetingTitlePlaceholder: "Reyinyon Ekip",
      descriptionPlaceholder: "Diskite sou mizajou pwojè...",
      locationPlaceholder: "Sal Konferans A"
    },

    // Tasks
    tasks: {
      title: "Travay",
      createTask: "Kreye Travay",
      editTask: "Modifye Travay",
      taskTitle: "Tit Travay",
      description: "Deskripsyon",
      assignTo: "Asiyen Bay",
      dueDate: "Dat Limit",
      priority: "Priyorite",
      status: "Estati",
      linkedDocument: "Dokiman Lye",
      allTasks: "Tout Travay",
      assignedToMe: "Asiyen Ban Mwen",
      createdByMe: "Kreye Pa Mwen",
      allStatuses: "Tout Estati",
      allPriorities: "Tout Priyorite",
      filterBy: "Filtre pa",
      noTasks: "Pa jwenn travay",
      noTasksHint: "Kreye yon nouvo travay pou kòmanse",
      total: "Total",
      overdue: "An reta",
      pending: "An atant",
      inProgress: "An kou",
      completed: "Fini",
      cancelled: "Anile",
      low: "Ba",
      medium: "Mwayen",
      high: "Wo",
      none: "Okenn",
      selectDocument: "Chwazi dokiman",
      selectTeamMember: "Chwazi manm ekip",
      titlePlaceholder: "Revize rapò trimès",
      descriptionPlaceholder: "Ajoute plis detay..."
    }
  }
};

export default translations;
